import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "../../lib/api";
import { Reveal } from "../site/Reveal";

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Signup is unavailable right now");
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setDone(true);
      setEmail("");
      toast.success("Welcome to the Luxe List!");
    } catch {
      toast.error("Please enter a valid email address");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="relative overflow-hidden bg-plum text-cream grain">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 py-20 sm:py-24 lg:grid-cols-[1.1fr_1fr]">
        <div className="relative z-10">
          <Reveal><p className="eyebrow text-gold">Join the</p></Reveal>
          <Reveal delay={0.1}><h2 data-testid="newsletter-heading" className="font-script text-7xl sm:text-8xl leading-[1.1] text-cream">Luxe List</h2></Reveal>
          <Reveal delay={0.2}><p className="mt-4 max-w-md text-base text-cream/85">Get exclusive offers, new arrivals and a little shopping inspiration straight to your inbox.</p></Reveal>
          <Reveal delay={0.3}>
            {done ? (
              <p data-testid="newsletter-success" className="mt-8 font-display text-2xl italic text-gold-light">You're on the list. Welcome, darling.</p>
            ) : (
              <form onSubmit={submit} className="mt-8 flex max-w-md overflow-hidden rounded-full bg-white p-1.5">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  data-testid="newsletter-email-input"
                  className="min-w-0 flex-1 bg-transparent px-5 text-sm text-plum placeholder:text-plum/50 focus:outline-none"
                />
                <button type="submit" disabled={busy} data-testid="newsletter-submit-button" className="inline-flex items-center gap-2 rounded-full bg-magenta px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-tangerine-deep disabled:opacity-70">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign up
                </button>
              </form>
            )}
          </Reveal>
        </div>
        <div className="relative h-[340px] sm:h-[400px]">
          <motion.img initial={{ rotate: -8, y: 30, opacity: 0 }} whileInView={{ rotate: -6, y: 0, opacity: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1 }} src="/images/gift-ribbon.jpg" alt="Gift box with pink ribbon" loading="lazy" className="absolute left-0 top-6 h-56 w-44 sm:h-72 sm:w-56 rounded-3xl object-cover shadow-2xl" />
          <motion.img initial={{ rotate: 10, y: 30, opacity: 0 }} whileInView={{ rotate: 6, y: 0, opacity: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1, delay: 0.15 }} src="/images/glitter-gifts.jpg" alt="Glitter gift boxes" loading="lazy" className="absolute right-0 top-0 h-52 w-40 sm:h-64 sm:w-52 rounded-3xl object-cover shadow-2xl" />
          <motion.div initial={{ scale: 0.6, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ type: "spring", stiffness: 140, delay: 0.3 }} className="absolute bottom-2 left-1/2 -translate-x-1/2 -rotate-6 rounded-2xl bg-teal px-8 py-5 text-center shadow-2xl">
            <p className="font-script text-4xl sm:text-5xl leading-none text-white">Exclusive Deals</p>
            <p className="mt-1 font-display italic text-gold-light">just for you!</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
